import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { ArrowLeft, Package, Receipt, Eye, Download, Loader } from 'lucide-react';
import './subscription.css';

// ---------- Appels API (même convention que sidebar.js : REACT_APP_API_URL + token dans localStorage) ----------
const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const getSubscription = () => api.get('/subscription/me').then((r) => r.data);
const getInvoices = () => api.get('/subscription/me/invoices').then((r) => r.data);
const getInvoicePdf = (invoiceId, action) =>
  api
    .get(`/invoices/${action}/${invoiceId}`, { responseType: 'blob' })
    .then((r) => new Blob([r.data], { type: 'application/pdf' }));

// ---------- Utilitaires ----------
// Si le backend n'envoie pas de statut, on considère la facture comme payée (comportement actuel).
const STATUS = {
  paid: { label: 'Paid', tone: 'ok' },
  pending: { label: 'Pending', tone: 'warn' },
  overdue: { label: 'Overdue', tone: 'ko' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
};

const fmt = (n) => `${Number(n).toFixed(0)} DT`;
const date = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// ---------- Coque du popup (rendue dans <body> via createPortal) ----------
function Shell({ onClose, children }) {
  const bodyRef = useRef(null);

  // Empêche la page (dashboard / sidebar) d'intercepter la molette ou le tactile
  // pendant qu'on fait défiler le contenu du popup.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const stop = (e) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: true });
    el.addEventListener('touchmove', stop, { passive: true });
    return () => {
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchmove', stop);
    };
  }, []);

  return createPortal(
    <>
      {/* Fond flouté, comme Facility VIP : un clic ferme le popup */}
      <div className="sub-backdrop" onClick={onClose} aria-hidden="true" />

      <div className="sub-popup" role="dialog" aria-modal="true" aria-labelledby="sub-title">
        <header className="sub-header">
          {onClose && (
            <button type="button" className="sub-back" onClick={onClose} aria-label="Back">
              <ArrowLeft size={18} />
            </button>
          )}
          <h1 id="sub-title" className="sub-title">Subscription</h1>
        </header>
        <div className="sub-body" ref={bodyRef}>{children}</div>
      </div>
    </>,
    document.body
  );
}

// ---------- Une ligne de facture (même style que les factures de Facility VIP) ----------
function InvoiceRow({ invoice, onView, onDownload }) {
  const status = STATUS[invoice.status || 'paid'] || STATUS.paid;
  const when = invoice.invoiceDate || invoice.issuedAt || invoice.createdAt;

  return (
    <li className="sub-item">
      <span className="sub-item__icon" aria-hidden="true">
        <Receipt size={15} />
      </span>

      <span className="sub-item__text">
        <strong>{invoice.number || `Invoice #${String(invoice._id).slice(-6)}`}</strong>
        <small>
          <b className={`sub-tone sub-tone--${status.tone}`}>{status.label}</b>
          {when ? ` · ${date(when)}` : ''}
          {invoice.amount != null ? ` · ${fmt(invoice.amount)}` : ''}
        </small>
      </span>

      {invoice.pdfPath && (
        <>
          <button type="button" className="sub-icon-btn" title="View" aria-label="View invoice" onClick={() => onView(invoice._id)}>
            <Eye size={14} />
          </button>
          <button type="button" className="sub-icon-btn" title="Download" aria-label="Download invoice" onClick={() => onDownload(invoice._id)}>
            <Download size={14} />
          </button>
        </>
      )}
    </li>
  );
}

// ---------- Popup Abonnement ----------
// Utilisation : <Subscription onClose={...} />  (onClose est optionnel : sans lui, pas de flèche dans le popup)
export default function Subscription({ onClose }) {
  const [sum, setSum] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(''); // erreur de chargement : remplace le contenu
  const [actionError, setActionError] = useState(''); // erreur PDF : affichée au-dessus de la liste

  const load = useCallback(async () => {
    try {
      const [subscriptionResponse, invoicesResponse] = await Promise.all([getSubscription(), getInvoices()]);
      setSum(subscriptionResponse);
      setInvoices(Array.isArray(invoicesResponse) ? invoicesResponse : invoicesResponse.invoices || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'Unable to load your information. Please try again.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const viewInvoice = async (invoiceId) => {
    setActionError('');
    try {
      const url = URL.createObjectURL(await getInvoicePdf(invoiceId, 'view'));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setActionError('Unable to open this invoice.');
    }
  };

  const downloadInvoice = async (invoiceId) => {
    setActionError('');
    try {
      const url = URL.createObjectURL(await getInvoicePdf(invoiceId, 'download'));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setActionError('Unable to download this invoice.');
    }
  };

  if (error) {
    return (
      <Shell onClose={onClose}>
        <p role="alert" className="sub-error">{error}</p>
      </Shell>
    );
  }

  if (!sum) {
    return (
      <Shell onClose={onClose}>
        <div className="sub-state">
          <Loader className="sub-spin" size={16} /> Loading…
        </div>
      </Shell>
    );
  }

  return (
    <Shell onClose={onClose}>
      <div className="sub-stats">
        <div className="sub-stat sub-stat--main">
          <div className="sub-stat__text">
            <span className="sub-stat__label">Packs purchased</span>
            <span className="sub-stat__value">{sum.totalPurchasedPacks ?? sum.totalValidatedPacks ?? 0}</span>
          </div>
          <span className="sub-stat__icon" aria-hidden="true">
            <Package size={16} />
          </span>
        </div>
        <div className="sub-stat">
          <span className="sub-stat__label">Total paid</span>
          <span className="sub-stat__value">{fmt(sum.totalPurchases ?? sum.totalPaid ?? sum.feesPaid ?? 0)}</span>
        </div>
      </div>

      <p className="sub-label">
        My invoices{invoices.length > 3 ? ` · ${invoices.length} (scroll)` : ''}
      </p>
      {actionError && <div role="alert" className="sub-error">{actionError}</div>}

      {invoices.length === 0 ? (
        <div className="sub-state">No invoices available yet.</div>
      ) : (
        <ul className="sub-list">
          {invoices.map((invoice) => (
            <InvoiceRow key={invoice._id} invoice={invoice} onView={viewInvoice} onDownload={downloadInvoice} />
          ))}
        </ul>
      )}
    </Shell>
  );
}